// A category's Service rules (CR_20260921_Asset_Lifecycle L17, §7.2): "6-monthly service — every 180 days, or every 120
// days on a project, whichever comes first". Ships empty (Q4): a category without a rule shows no Service card.
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { sameKind } from '@fleetspoke/core/lifecycle';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../auth/session';
import { useDeleteServiceRule, useSaveServiceRule, useServiceRules } from '../data/service';

const days = (v: string) => (v.trim() === '' ? null : Math.max(1, Math.round(Number(v))));

export function ServiceRulesTab({ categoryId }: { categoryId: string }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const rules = useServiceRules();
  const save = useSaveServiceRule(me!.company!.id);
  const del = useDeleteServiceRule();
  const mine = (rules.data ?? []).filter((r) => r.category_id === categoryId);
  const [task, setTask] = useState('');
  const [every, setEvery] = useState('');
  const [everyUsed, setEveryUsed] = useState('');
  const taken = mine.some((r) => sameKind(r.task, task));
  const noInterval = days(every) == null && days(everyUsed) == null;
  const add = () =>
    save.mutate(
      {
        category_id: categoryId,
        task,
        interval_days: days(every),
        interval_deployed_days: days(everyUsed),
      },
      {
        onSuccess: () => {
          setTask('');
          setEvery('');
          setEveryUsed('');
        },
      },
    );
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('svc.intro')}
      </Typography>
      {mine.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('svc.task')}</TableCell>
              <TableCell>{t('svc.every_days')}</TableCell>
              <TableCell>{t('svc.every_deployed')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {mine.map((r) => (
              <TableRow key={r.id} data-testid="service-rule-row">
                <TableCell sx={{ fontWeight: 500 }}>{r.task}</TableCell>
                <TableCell>{r.interval_days ?? '—'}</TableCell>
                <TableCell>{r.interval_deployed_days ?? '—'}</TableCell>
                <TableCell align="right" sx={{ width: 56 }}>
                  <Tooltip title={t('certs.rule_delete')}>
                    <IconButton
                      size="small"
                      aria-label={t('certs.rule_delete')}
                      disabled={del.isPending}
                      onClick={() => del.mutate(r.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography color="text.secondary" data-testid="service-rules-none">
          {t('svc.rules_none')}
        </Typography>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}
      >
        <TextField
          size="small"
          label={t('svc.task')}
          value={task}
          onChange={(e) => setTask(e.target.value)}
          error={taken}
          helperText={taken ? t('svc.task_taken') : undefined}
          sx={{ flex: 1, minWidth: 180 }}
          slotProps={{ htmlInput: { maxLength: 60, 'data-testid': 'service-rule-task' } }}
        />
        <TextField
          size="small"
          type="number"
          label={t('svc.every_days')}
          value={every}
          onChange={(e) => setEvery(e.target.value)}
          sx={{ width: { md: 140 } }}
          slotProps={{ htmlInput: { min: 1, max: 3650, 'data-testid': 'service-rule-days' } }}
        />
        <TextField
          size="small"
          type="number"
          label={t('svc.every_deployed')}
          value={everyUsed}
          onChange={(e) => setEveryUsed(e.target.value)}
          sx={{ width: { md: 200 } }}
          slotProps={{ htmlInput: { min: 1, max: 3650, 'data-testid': 'service-rule-used' } }}
        />
        <Button
          variant="contained"
          disabled={!task.trim() || taken || noInterval || save.isPending}
          onClick={add}
          data-testid="service-rule-add"
        >
          {t('svc.rule_add')}
        </Button>
      </Stack>
      {task.trim() && noInterval ? (
        <Typography variant="caption" color="text.secondary">
          {t('svc.need_interval')}
        </Typography>
      ) : null}
      {save.error ? (
        <Alert severity="error">
          {t('common.error_generic')} {(save.error as Error).message}
        </Alert>
      ) : null}
    </Stack>
  );
}
